/**
 * Get the Look — Section 1 drawer behavior
 *
 * Fixes addressed in this version:
 *
 * 1. SAFARI TOGGLE-BUTTON FALLBACK
 *    is="toggle-button" is a "customised built-in" custom element — Chrome/Firefox
 *    support it natively; Safari ignores it and leaves the button as a plain
 *    HTMLButtonElement. We detect this and add a direct click→drawer.open=true
 *    handler only when needed, leaving Chrome's own ToggleButton to handle it
 *    (so we don't double-toggle on the second click).
 *
 * 2. SIZE-PILL → VARIANT SYNC
 *    Radio values are now real variant IDs (Liquid fix: `where: look_size_opt_key`).
 *    The change handler copies the checked radio's value into the hidden #id input.
 *
 * 3. AJAX ADD-TO-CART
 *    Submits via /cart/add.js with sections:['mini-cart'] so we can pass the
 *    already-rendered mini-cart HTML back in the cart:refresh event, saving the
 *    CartDrawer from doing a second round-trip fetch.
 *    Drawer intentionally stays open so the shopper can add multiple items.
 *
 * 4. CART:REFRESH TARGET
 *    Must be dispatched on document.documentElement (the <html> element).
 *    CartDrawer and CartCount both listen via rootDelegate which is bound to
 *    document.documentElement — events dispatched on `document` never bubble
 *    up to document.documentElement.
 *
 * 5. BUTTON LOADING STATE
 *    Managed manually (no is="loader-button") to avoid the Safari path in
 *    LoaderButton that calls form.submit() directly, bypassing JS submit events.
 *
 * 6. RT SIZE CHART APP RE-TRIGGER
 *    The RT app typically scans for .product-form__option-name on DOMContentLoaded.
 *    Because our labels are inside a hidden drawer, the app may miss them.
 *    We dispatch a 'rt:reinit' synthetic event (common RT convention) and fall
 *    back to re-scanning via MutationObserver once on the first drawer open.
 */

(function () {
  'use strict';

  /**
   * Detect whether the browser actually upgrades customised built-in elements
   * (is="toggle-button"). Chrome/Firefox do; Safari does not.
   */
  function needsToggleButtonFallback() {
    var ToggleBtnCtor = window.customElements && customElements.get('toggle-button');
    if (!ToggleBtnCtor) return true; // theme.js not loaded yet — add fallback to be safe
    try {
      var testEl = document.createElement('button', { is: 'toggle-button' });
      return !(testEl instanceof ToggleBtnCtor);
    } catch (e) {
      return true;
    }
  }

  var safariMode = needsToggleButtonFallback();
  var rtReinitDispatched = false;

  /**
   * Wire up a single .get-the-look-drawer element.
   * Called once per drawer on DOMContentLoaded.
   */
  function initLookDrawer(drawerEl) {
    var drawerId = drawerEl.id;

    // ── 1. THUMBNAIL CLICK → OPEN DRAWER (Safari fallback) ─────────────────
    if (safariMode) {
      var triggers = document.querySelectorAll(
        '[data-get-the-look-trigger][aria-controls="' + drawerId + '"]'
      );
      triggers.forEach(function (btn) {
        btn.addEventListener('click', function () {
          drawerEl.open = true;
        });
      });
    }

    // Keep aria-expanded in sync on all trigger buttons whenever the drawer
    // opens or closes (important for both Chrome and Safari).
    document.addEventListener('openable-element:open', function (evt) {
      if (evt.target === drawerEl) {
        document.querySelectorAll('[aria-controls="' + drawerId + '"]').forEach(function (btn) {
          btn.setAttribute('aria-expanded', 'true');
        });
        maybeReinitRtApp(drawerEl);
      }
    });
    document.addEventListener('openable-element:close', function (evt) {
      if (evt.target === drawerEl) {
        document.querySelectorAll('[aria-controls="' + drawerId + '"]').forEach(function (btn) {
          btn.setAttribute('aria-expanded', 'false');
        });
      }
    });

    // ── 2. SIZE PILL → SYNC HIDDEN VARIANT ID INPUT ─────────────────────────
    drawerEl.querySelectorAll('[data-look-size-options]').forEach(function (optionGroup) {
      optionGroup.addEventListener('change', function (evt) {
        var row = evt.target.closest('[data-look-product-id]');
        if (!row) return;
        var hiddenInput = row.querySelector('[data-look-variant-id]');
        if (!hiddenInput) return;
        hiddenInput.value = evt.target.value;

        // Update the submit button availability in case it was disabled.
        var btn = row.querySelector('[type="submit"]');
        if (btn && evt.target.value && !evt.target.disabled) {
          btn.disabled = false;
          btn.textContent = 'Add to Bag';
        }
      });
    });

    // ── 3. AJAX ADD-TO-CART (drawer stays open) ─────────────────────────────
    drawerEl.querySelectorAll('[data-look-add-form]').forEach(function (form) {
      form.addEventListener('submit', function (evt) {
        evt.preventDefault();

        var submitBtn = form.querySelector('button[type="submit"]');
        var variantInput = form.querySelector('[data-look-variant-id]');
        var variantId = variantInput ? variantInput.value : '';

        if (!variantId) {
          // No size selected — give visual feedback
          submitBtn.textContent = 'Select a size';
          setTimeout(function () {
            submitBtn.textContent = 'Add to Bag';
          }, 1500);
          return;
        }

        var originalText = submitBtn.textContent;
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
        submitBtn.textContent = 'Adding…';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1, sections: ['mini-cart'] }),
        })
          .then(function (res) {
            if (!res.ok) {
              return res.json().then(function (err) { throw err; });
            }
            return res.json();
          })
          .then(function (addResponse) {
            submitBtn.textContent = 'Added ✓';

            // Fetch the current cart state so CartCount updates immediately.
            // Merge the already-rendered mini-cart HTML (from addResponse.sections)
            // into the cart data so CartDrawer doesn't need a second server round-trip.
            fetch('/cart.js')
              .then(function (r) { return r.json(); })
              .then(function (cartData) {
                if (addResponse.sections) {
                  cartData.sections = addResponse.sections;
                }
                // ── CRITICAL: dispatch on document.documentElement, not document ──
                // CartDrawer and CartCount listen via rootDelegate which is bound to
                // document.documentElement. Events on `document` never reach it.
                document.documentElement.dispatchEvent(
                  new CustomEvent('cart:refresh', {
                    bubbles: true,
                    detail: {
                      cart: cartData,
                      openMiniCart: false, // keep the GTL drawer open
                    },
                  })
                );
              })
              .catch(function () {
                // Even if the cart fetch fails, dispatch a bare cart:refresh so
                // CartDrawer/CartCount do their own fallback fetch.
                document.documentElement.dispatchEvent(
                  new CustomEvent('cart:refresh', { bubbles: true, detail: { openMiniCart: false } })
                );
              });

            setTimeout(function () {
              submitBtn.textContent = originalText;
              submitBtn.disabled = false;
              submitBtn.removeAttribute('aria-busy');
            }, 1500);
          })
          .catch(function (err) {
            console.error('Get the Look — add to cart failed:', err);
            var msg = (err && err.description) ? err.description : 'Could not add — try again';
            submitBtn.textContent = msg;
            setTimeout(function () {
              submitBtn.textContent = originalText;
              submitBtn.disabled = false;
              submitBtn.removeAttribute('aria-busy');
            }, 2000);
          });
      });
    });
  }

  /**
   * Attempt to re-trigger the RT: Size Chart app's scanner once the drawer
   * opens for the first time. Most RT app versions respond to one of:
   *   • A 'rt:reinit' CustomEvent on document
   *   • window.RTSizeChart.init()
   *   • Re-adding .product-form__option-name nodes to the DOM (MutationObserver)
   *
   * Because the app is closed-source we try all safe approaches.
   */
  function maybeReinitRtApp(drawerEl) {
    if (rtReinitDispatched) return;
    rtReinitDispatched = true;

    // Option A — common event name used by RT and similar apps
    document.dispatchEvent(new CustomEvent('rt:reinit', { bubbles: true }));

    // Option B — if the app exposes a global init function
    if (window.RTSizeChart && typeof window.RTSizeChart.init === 'function') {
      window.RTSizeChart.init();
    }

    // Option C — if the app uses a MutationObserver on the document, simply
    // touching (re-appending) the labels will fire it. We do this by moving the
    // option-info divs to themselves — a no-op that triggers the observer.
    drawerEl.querySelectorAll('.product-form__option-info').forEach(function (el) {
      el.parentNode.insertBefore(el, el.nextSibling);
    });
  }

  // ── INITIALISE ─────────────────────────────────────────────────────────────
  // The <script defer> tag ensures this runs after HTML parsing and after
  // theme.js (which defines custom elements), but before DOMContentLoaded.
  // Wrapping in DOMContentLoaded is still safe and harmless.
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.get-the-look-drawer').forEach(initLookDrawer);
  });
})();
